// src/components/ui/Layout/Card.tsx
import React from 'react';
import { formatPercent } from '@/utils/Formatters';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './Card.module.css';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  background?: 'white' | 'light-green' | 'light-blue' | 'light-gray' | 'transparent';
  contentFit?: 'fixed' | 'auto';
  contentBreak?: 'normal' | 'all' | 'truncate';
  contentSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'auto';
  responsive?: boolean; // Свойство для активации адаптивных стилей
  onClick?: () => void; // Добавляем поддержку обработчика клика
}

export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export interface CardBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Body: React.FC<CardBodyProps>;
} = ({
  children,
  className = '',
  padding = 'md',
  shadow = 'sm',
  background = 'white',
  contentFit = 'fixed',
  contentBreak = 'normal',
  contentSize = 'md',
  responsive = true, // По умолчанию используем адаптивные стили
  onClick // Используем переданный обработчик клика
}) => {
  const { isMobile } = useResponsive();
  
  // Получаем класс для отступов
  const getPaddingClass = () => {
    if (!responsive) {
      return styles[`cardPadding${padding.charAt(0).toUpperCase() + padding.slice(1)}`];
    }
    
    // Для мобильных используем меньшие отступы
    if (isMobile && responsive) {
      switch(padding) {
        case 'lg': return styles.cardPaddingMd;
        case 'md': return styles.cardPaddingSm;
        case 'sm': return styles.cardPaddingSm;
        default: return styles.cardPaddingNone;
      }
    }
    
    return styles[`cardPadding${padding.charAt(0).toUpperCase() + padding.slice(1)}`];
  };

  // Получаем класс для тени
  const getShadowClass = () => {
    return styles[`cardShadow${shadow.charAt(0).toUpperCase() + shadow.slice(1)}`];
  };

  // Получаем класс для фона
  const getBackgroundClass = () => {
    switch (background) {
      case 'white': return styles.cardBgWhite;
      case 'light-green': return styles.cardBgLightGreen;
      case 'light-blue': return styles.cardBgLightBlue;
      case 'light-gray': return styles.cardBgLightGray;
      case 'transparent': return styles.cardBgTransparent;
      default: return styles.cardBgWhite;
    }
  };

  // Получаем класс для настройки контента
  const getContentFitClass = () => {
    return contentFit === 'auto' ? styles.contentAuto : '';
  };

  // Получаем класс для переноса текста
  const getContentBreakClass = () => {
    switch (contentBreak) {
      case 'all': return styles.contentBreakWords;
      case 'truncate': return styles.contentTruncate;
      default: return '';
    }
  };

  // Получаем класс для размера текста с учетом адаптивности
  const getContentSizeClass = () => {
    if (!responsive) {
      return contentSize !== 'md' ? styles[`text${contentSize.charAt(0).toUpperCase() + contentSize.slice(1)}`] : '';
    }
    
    // Для мобильных уменьшаем размер текста
    if (isMobile) {
      switch(contentSize) {
        case 'xl': return styles.textLg;
        case 'lg': return styles.textBase;
        case 'md': return styles.textSm;
        case 'sm': return styles.textXs;
        case 'auto': return styles.textAutoFit;
        default: return '';
      }
    }
    
    return contentSize !== 'md' ? styles[`text${contentSize.charAt(0).toUpperCase() + contentSize.slice(1)}`] : '';
  };

  // Добавляем класс для интерактивной карточки, если есть обработчик клика
  const interactiveClass = onClick ? styles.cardInteractive : '';

  // Базовый класс карточки
  const cardClasses = [
    styles.card,
    getPaddingClass(),
    getShadowClass(),
    getBackgroundClass(),
    interactiveClass,
    className
  ].filter(Boolean).join(' ');

  // Классы для контента
  const contentClasses = [
    getContentFitClass(),
    getContentBreakClass(),
    getContentSizeClass()
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={cardClasses}
      data-content-fit={contentFit}
      data-content-break={contentBreak}
      data-content-size={contentSize}
      onClick={onClick} // Добавляем обработчик клика
    >
      {contentFit === 'auto' || contentBreak !== 'normal' || contentSize !== 'md' 
        ? <div className={contentClasses}>{children}</div>
        : children
      }
    </div>
  );
};

// Компонент CardHeader
export const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => {
  return (
    <div className={`${styles.cardHeader} ${className}`}>
      {children}
    </div>
  );
};

// Компонент CardBody
export const CardBody: React.FC<CardBodyProps> = ({ children, className = '' }) => {
  return (
    <div className={`${styles.cardBody} ${className}`}>
      {children}
    </div>
  );
};

// Прикрепляем подкомпоненты к Card
Card.Header = CardHeader;
Card.Body = CardBody;

// Компонент MetricCard с адаптивным дизайном
export interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: number | string;
  onClick?: () => void;
  isSelected?: boolean;
  className?: string;
  padding?: CardProps['padding'];
  shadow?: CardProps['shadow'];
  background?: CardProps['background'];
  contentFit?: CardProps['contentFit'];
  contentBreak?: CardProps['contentBreak'];
  contentSize?: CardProps['contentSize'];
  responsive?: CardProps['responsive'];
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  trend = 'neutral',
  trendValue,
  onClick,
  isSelected = false,
  className = '',
  shadow = 'sm',
  background = 'white',
  contentFit = 'auto',
  contentBreak = 'all',
  contentSize = 'auto',
  responsive = true
}) => {
  const { isMobile } = useResponsive();
  
  // Получаем класс стиля для тренда
  const getTrendClass = () => {
    switch (trend) {
      case 'up': return styles.trendUp;
      case 'down': return styles.trendDown;
      default: return styles.trendNeutral;
    }
  };
  
  // Формируем класс для подсветки выбранной карточки
  const selectedClass = isSelected ? styles.metricCardSelected : '';
  
  // Формируем класс для ховер-эффекта
  const hoverClass = !isSelected ? styles.metricCardHover : '';

  return (
    <Card 
      padding="none"
      shadow={shadow}
      background={background}
      contentFit={contentFit}
      contentBreak={contentBreak}
      contentSize={contentSize}
      responsive={responsive}
      className={`${styles.metricCard} ${hoverClass} ${selectedClass} ${className}`}
      onClick={onClick}
    >
      <div 
        className={styles.metricContent}
        style={{ padding: isMobile && responsive ? '0.5rem' : '0.75rem' }}
      >
        <div className={styles.metricTitle}>
          {title}
        </div>
        
        <div>
          <div className={styles.metricValue}>
            {value}
          </div>
          
          <div className={styles.metricTrend}>
            {trendValue !== undefined && (
              <span className={getTrendClass()}>
                {trend === 'up' ? '↑ ' : trend === 'down' ? '↓ ' : ''}
                {typeof trendValue === 'number' ? (
                  <>
                    {trendValue > 0 ? '+' : ''}
                    {formatPercent(trendValue, 1)}
                  </>
                ) : (
                  trendValue
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default Card;